/* eslint-disable no-restricted-imports */
import React from 'react'

import PropTypes from 'prop-types'

import { Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

import createLogger from '~/src/Lib/Logging'
import { renderableType } from '~/src/Lib/PropTypes'
import SimpleSelect from '~/src/UI/Shared/Form/SimpleSelect'

import { optionShape, valueShape } from './utils'

const DefaultValueRenderer = ({ classes, value, options }) => {
  const labels = options.filter(option => (
    Array.isArray(value) ? value.includes(option.value) : value === option.value
  )).map(option => option.label)

  return (
    <Typography
      color="primary"
      variant="subtitle1"
      className={classes.label}
    >
      {labels.length > 2 ? `${labels.length} selected` : labels.map(label => (
        <span key={label}>{label}</span>
      ))}
    </Typography>
  )
}
const valuePropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
])
DefaultValueRenderer.propTypes = {
  value: PropTypes.oneOfType([valuePropType, PropTypes.arrayOf(valuePropType)]).isRequired,
  options: PropTypes.arrayOf(optionShape).isRequired,
}

const displayName = 'RC/Dropdown'
const logger = createLogger(displayName)

const styles = theme => ({
  root: {
    minWidth: '15rem',
    [theme.breakpoints.only('sm')]: {
      minWidth: '10rem',
      '& label': {
        fontSize: theme.typography.body2.fontSize,
        letterSpacing: theme.typography.body2.letterSpacing,
        lineHeight: theme.typography.body2.lineHeight,
        paddingTop: theme.spacing(0.5),
      },
    },
    [theme.breakpoints.down('sm')]: {
      width: `calc(100% - ${theme.spacing()}px)`,
    },
  },
  icon: {
    position: 'relative',
    top: 6,
  },
  label: {
    display: 'inline-block',
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.body1.fontSize,
      letterSpacing: theme.typography.body1.letterSpacing,
      lineHeight: theme.typography.body1.lineHeight,
    },
  },
})

export const useStyles = makeStyles(styles, { name: displayName })

export const DropdownComponent = ({
  classes: classesProp,
  disabled,
  multiple = true,
  options,
  selectAll = false,
  selectAllLabel = '',
  value = null,
  ValueRenderer = DefaultValueRenderer,
  Button: ignore1, // eslint-disable-line react/prop-types
  vertical: ignore2, // eslint-disable-line react/prop-types
  ...passthrough
}) => {
  const classes = useStyles({ classes: classesProp })

  return (
    <SimpleSelect
      data-testid="responsive-chooser-dropdown"
      disabled={disabled || !options?.length}
      options={options}
      multiple={multiple}
      selectAll={selectAll}
      selectAllLabel={selectAllLabel}
      value={value}
      renderValue={v => (
        <ValueRenderer value={v} options={options} classes={classes} />
      )}
      {...passthrough}
    />
  )
}

DropdownComponent.propTypes = {
  disabled: PropTypes.bool.isRequired,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(optionShape).isRequired,
  selectAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  value: valueShape,
  ValueRenderer: renderableType,
  onChange: PropTypes.func.isRequired,
}
DropdownComponent.displayName = displayName

export default React.memo(DropdownComponent)
